import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, AED_short, getWorkspace } from '../lib/api';
import { motion } from 'framer-motion';
import {
  MapPin, Loader2, RefreshCcw, ExternalLink, Home, Filter, X,
  BedDouble, Ruler, CheckCircle2, ImageIcon, Search, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const PROP_TYPES = ['apartment', 'villa', 'townhouse', 'penthouse', 'studio'];
const COMMERCIAL_TYPES = ['office', 'shop', 'warehouse', 'showroom', 'floor', 'building', 'factory', 'coworking'];
const SORTS = [
  { k: 'listed_desc', label: 'Newest' },
  { k: 'psf_asc', label: 'Best AED/sqft' },
  { k: 'price_asc', label: 'Price ↑' },
  { k: 'price_desc', label: 'Price ↓' },
];

function ListingCard({ l }) {
  const psf = l.asking_aed_per_sqft ? Math.round(l.asking_aed_per_sqft).toLocaleString() : '—';
  const cover = l.cover_photo_url;
  const isOffPlan = l.completion_status === 'off_plan';
  const isPFC = l.source === 'propertyfinder_commercial';
  const isPF = l.source === 'propertyfinder';
  const isRent = l.listing_type === 'rent';
  const sourceLabel = isPFC ? 'PF-C' : (isPF ? 'PF' : 'Bayut');
  const sourceHost = isPFC ? 'PropertyFinder' : (isPF ? 'PropertyFinder' : 'Bayut');
  const badgeCls = isPFC ? 'bg-indigo-500/90 text-white'
                          : (isPF ? 'bg-orange-500/90 text-white' : 'bg-slate-900/70 text-white');
  const openBayut = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (l.listing_url) window.open(l.listing_url, '_blank', 'noopener,noreferrer');
  };
  const priceSuffix = isRent && l.rent_frequency ? `/${String(l.rent_frequency).toLowerCase().slice(0,2)}` : '';
  return (
    <div
      data-testid={`live-listing-${l.id}`}
      className="glass rounded-2xl overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition group flex flex-col"
    >
      <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden cursor-pointer" onClick={openBayut}>
        {cover ? (
          <img src={cover} alt={l.title || l.building} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300"><ImageIcon size={28}/></div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          {l.verified && (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/90 text-white font-semibold flex items-center gap-1">
              <CheckCircle2 size={9}/> Verified
            </span>
          )}
          {isOffPlan && (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/90 text-white font-semibold">
              Off-plan
            </span>
          )}
          {isPFC && (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-600/95 text-white font-semibold">
              Commercial
            </span>
          )}
          {isRent && (
            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-sky-600/95 text-white font-semibold">
              For rent
            </span>
          )}
        </div>
        <div className={`absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] backdrop-blur-sm inline-flex items-center gap-1 shadow-sm ${badgeCls}`}>
          <ExternalLink size={9}/> {sourceLabel}
        </div>
      </div>
      <div className="p-3 flex-1 flex flex-col cursor-pointer" onClick={openBayut}>
        <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1 flex items-center gap-1">
          <MapPin size={10}/>{l.community || l.sub_community || l.emirate || '—'}
        </div>
        <div className="text-sm font-medium text-slate-800 line-clamp-1">{l.title || l.building || '—'}</div>
        <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-3">
          <span className="flex items-center gap-1"><BedDouble size={10}/>{l.bedrooms === 0 ? 'Studio' : (l.bedrooms || '—')}</span>
          <span className="flex items-center gap-1"><Ruler size={10}/>{l.size_sqft ? Math.round(l.size_sqft).toLocaleString() : '—'} sqft</span>
        </div>
        <div className="mt-auto pt-3 flex items-end justify-between">
          <div>
            <div className="font-serif text-lg font-bold text-slate-900">{AED_short(l.asking_price_aed)}<span className="text-xs text-slate-400 font-sans">{priceSuffix}</span></div>
            <div className="text-[10px] text-slate-500">AED {psf}/sqft</div>
          </div>
          {l.agency_name && (
            <div className="text-[9px] text-slate-400 max-w-[8rem] text-right truncate" title={l.agency_name}>
              {l.agency_name}
            </div>
          )}
        </div>
      </div>
      {/* Action row — outside clickable area */}
      <div className="border-t border-slate-100/80 flex divide-x divide-slate-100/80">
        <Link
          to={`/app/explain/listing/${l.id}`}
          onClick={(e) => e.stopPropagation()}
          data-testid={`live-listing-analyze-${l.id}`}
          className="flex-1 py-2 text-[11px] text-[#679286] hover:bg-[#679286]/10 font-semibold inline-flex items-center justify-center gap-1 transition">
          <Sparkles size={11}/> Analyze with Iris
        </Link>
        <button
          onClick={openBayut}
          data-testid={`live-listing-open-${l.id}`}
          className="flex-1 py-2 text-[11px] text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center gap-1 transition">
          <ExternalLink size={11}/> View on {sourceHost}
        </button>
      </div>
    </div>
  );
}

export default function LiveListingsPanel() {
  // Read URL params synchronously at initialization so mount-effects don't
  // cause a re-render cascade (was triggering "Maximum update depth" warnings).
  const _urlParams = React.useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    return {
      community: sp.get('community') || null,
      type: (sp.get('type') === 'rent' || sp.get('type') === 'sale') ? sp.get('type') : null,
    };
  }, []);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [selectedCommunities, setSelectedCommunities] = useState(() => _urlParams.community ? [_urlParams.community] : []);
  const [communityList, setCommunityList] = useState([]);
  const [showAllComms, setShowAllComms] = useState(false);
  const [propertyType, setPropertyType] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [completion, setCompletion] = useState('');
  const [sort, setSort] = useState('listed_desc');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minSqft, setMinSqft] = useState('');
  const [maxSqft, setMaxSqft] = useState('');
  const [sourceFilter, setSourceFilter] = useState(''); // '' | 'bayut' | 'propertyfinder' | 'propertyfinder_commercial'
  const [segment, setSegment] = useState(''); // '' | 'residential' | 'commercial'
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingPF, setRefreshingPF] = useState(false);
  const [refreshingPFC, setRefreshingPFC] = useState(false);
  const [listingTypeMode, setListingTypeMode] = useState(() => _urlParams.type || 'sale');
  const isLive = getWorkspace() === 'live';

  // Load community list once (for chip filter)
  useEffect(() => {
    api.get('/trends/listing-communities')
      .then(r => setCommunityList(r.data || []))
      .catch(() => {});
  }, []);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({
        listing_type: listingTypeMode, page: String(page), page_size: '24', sort,
      });
      if (segment) params.set('segment', segment);
      if (selectedCommunities.length > 0) params.set('communities', selectedCommunities.join(','));
      if (propertyType) params.set('property_type', propertyType);
      if (bedrooms !== '') params.set('bedrooms', bedrooms);
      if (completion) params.set('completion_status', completion);
      if (minPrice) params.set('min_price', minPrice);
      if (maxPrice) params.set('max_price', maxPrice);
      if (minSqft) params.set('min_sqft', minSqft);
      if (maxSqft) params.set('max_sqft', maxSqft);
      if (sourceFilter) params.set('source', sourceFilter);
      const r = await api.get(`/trends/listings?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load listings');
    } finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [page, selectedCommunities, propertyType, bedrooms, completion, sort, minPrice, maxPrice, minSqft, maxSqft, sourceFilter, segment, listingTypeMode]);

  const toggleCommunity = (c) => {
    setSelectedCommunities(prev =>
      prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]
    );
    setPage(1);
  };

  const clearAllFilters = () => {
    setSelectedCommunities([]); setPropertyType(''); setBedrooms('');
    setCompletion(''); setMinPrice(''); setMaxPrice('');
    setMinSqft(''); setMaxSqft(''); setPage(1);
  };

  const activeFilterCount = (selectedCommunities.length > 0 ? 1 : 0)
    + (propertyType ? 1 : 0) + (bedrooms !== '' ? 1 : 0)
    + (completion ? 1 : 0)
    + (minPrice || maxPrice ? 1 : 0) + (minSqft || maxSqft ? 1 : 0);

  // Client-side text filter for title/building
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter(x => (x.title || '').toLowerCase().includes(s)
      || (x.building || '').toLowerCase().includes(s)
      || (x.community || '').toLowerCase().includes(s));
  }, [items, q]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/trends/listings/refresh');
      toast.info('Live listings sync started — grabbing up to 10K rows from Bayut. This runs in the background (~2-3 min).');
      // Poll status
      const t0 = Date.now();
      const poll = setInterval(async () => {
        try {
          const s = await api.get('/trends/sync-status');
          const job = (s.data.jobs || []).find(j => j.job === 'listings_refresh');
          if (job && job.status === 'completed') {
            clearInterval(poll);
            setRefreshing(false);
            toast.success(job.message || 'Listings sync complete.');
            setPage(1);
            await load();
          } else if (job && job.status === 'failed') {
            clearInterval(poll);
            setRefreshing(false);
            toast.error(job.error || 'Sync failed.');
          } else if (Date.now() - t0 > 6 * 60 * 1000) {
            clearInterval(poll);
            setRefreshing(false);
            toast.warning('Sync still running — reload the page shortly to see results.');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setRefreshing(false);
      toast.error(e.response?.data?.detail || 'Failed to start sync');
    }
  };

  const refreshPF = async () => {
    setRefreshingPF(true);
    try {
      await api.post('/trends/propertyfinder/refresh');
      toast.info('PropertyFinder sync started — pulling up to 40 pages (~1000 listings). Runs in background.');
      const t0 = Date.now();
      const poll = setInterval(async () => {
        try {
          const s = await api.get('/trends/sync-status');
          const job = (s.data.jobs || []).find(j => j.job === 'propertyfinder_refresh');
          if (job && job.status === 'completed') {
            clearInterval(poll);
            setRefreshingPF(false);
            toast.success(job.message || 'PropertyFinder sync complete.');
            setPage(1);
            await load();
          } else if (job && job.status === 'failed') {
            clearInterval(poll);
            setRefreshingPF(false);
            toast.error(job.error || 'PropertyFinder sync failed.');
          } else if (Date.now() - t0 > 6 * 60 * 1000) {
            clearInterval(poll);
            setRefreshingPF(false);
            toast.warning('PF sync still running — reload later.');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setRefreshingPF(false);
      toast.error(e.response?.data?.detail || 'Failed to start PF sync');
    }
  };

  const refreshPFC = async () => {
    setRefreshingPFC(true);
    try {
      await api.post('/trends/propertyfinder-commercial/refresh');
      toast.info('Commercial sync started — pulling offices, retail, warehouses across Dubai. Runs in background (~2-4 min).');
      const t0 = Date.now();
      const poll = setInterval(async () => {
        try {
          const s = await api.get('/trends/sync-status');
          const job = (s.data.jobs || []).find(j => j.job === 'propertyfinder_commercial_refresh');
          if (job && job.status === 'completed') {
            clearInterval(poll);
            setRefreshingPFC(false);
            toast.success(job.message || 'Commercial sync complete.');
            setPage(1);
            await load();
          } else if (job && job.status === 'failed') {
            clearInterval(poll);
            setRefreshingPFC(false);
            toast.error(job.error || 'Commercial sync failed.');
          } else if (Date.now() - t0 > 8 * 60 * 1000) {
            clearInterval(poll);
            setRefreshingPFC(false);
            toast.warning('Commercial sync still running — reload later.');
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (e) {
      setRefreshingPFC(false);
      toast.error(e.response?.data?.detail || 'Failed to start Commercial sync');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 24));

  return (
    <div className="glass p-3 sm:p-6 mb-6" data-testid="live-listings-panel">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex-1 min-w-[16rem]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#679286]/15 text-[#679286]">
              Dubai · {total.toLocaleString()} active{sourceFilter ? ` · ${sourceFilter === 'bayut' ? 'Bayut only' : 'PropertyFinder only'}` : ' · all sources'}
            </span>
          </div>
        </div>
        {isLive && (
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto">
            <button onClick={refresh} disabled={refreshing}
              data-testid="listings-refresh-btn"
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white/85 border border-white/70 text-xs hover:bg-white disabled:opacity-60">
              {refreshing ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              <span className="hidden sm:inline">{refreshing ? 'Bayut…' : 'Sync Bayut'}</span>
              <span className="sm:hidden">Bayut</span>
            </button>
            <button onClick={refreshPF} disabled={refreshingPF}
              data-testid="listings-refresh-pf-btn"
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-orange-500/90 border border-orange-500 text-white text-xs hover:bg-orange-500 disabled:opacity-60">
              {refreshingPF ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              <span className="hidden sm:inline">{refreshingPF ? 'PF…' : 'Sync PropertyFinder'}</span>
              <span className="sm:hidden">PF</span>
            </button>
            <button onClick={refreshPFC} disabled={refreshingPFC}
              data-testid="listings-refresh-pfc-btn"
              className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-indigo-600 border border-indigo-600 text-white text-xs hover:bg-indigo-700 disabled:opacity-60">
              {refreshingPFC ? <Loader2 size={12} className="animate-spin"/> : <RefreshCcw size={12}/>}
              <span className="hidden sm:inline">{refreshingPFC ? 'Commercial…' : 'Sync Commercial'}</span>
              <span className="sm:hidden">Com.</span>
            </button>
          </div>
        )}
      </div>

      {/* Segment (residential vs commercial) + sale/rent tabs — horizontal scroll on mobile */}
      <div className="mb-3 flex items-center gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1 snap-x snap-mandatory">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1 flex-shrink-0">Segment</span>
        {[
          { k: '', label: 'All' },
          { k: 'residential', label: 'Residential', color: 'bg-[#679286]' },
          { k: 'commercial', label: 'Commercial', color: 'bg-indigo-600' },
        ].map(s => {
          const active = segment === s.k;
          return (
            <button key={s.k || 'all'}
              onClick={() => { setSegment(s.k); setPropertyType(''); setPage(1); }}
              data-testid={`listings-segment-${s.k || 'all'}`}
              className={`px-3 py-1 rounded-full text-xs transition border ${
                active ? `${s.color || 'bg-slate-800'} text-white border-transparent shadow-sm`
                       : 'bg-white/85 hover:bg-white border-white/70 text-slate-700'
              }`}>
              {s.label}
            </button>
          );
        })}
        <span className="mx-2 text-slate-300">·</span>
        <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Type</span>
        {[
          { k: 'sale', label: 'For sale' },
          { k: 'rent', label: 'For rent' },
        ].map(s => {
          const active = listingTypeMode === s.k;
          return (
            <button key={s.k}
              onClick={() => { setListingTypeMode(s.k); setPage(1); }}
              data-testid={`listings-type-mode-${s.k}`}
              className={`px-3 py-1 rounded-full text-xs transition border ${
                active ? 'bg-slate-800 text-white border-transparent shadow-sm'
                       : 'bg-white/85 hover:bg-white border-white/70 text-slate-700'
              }`}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Source segmented filter — horizontal scroll on mobile */}
      <div className="mb-4 flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1 flex-shrink-0">Source</span>
        {[
          { k: '', label: 'All sources', color: 'bg-slate-800' },
          { k: 'bayut', label: 'Bayut only', color: 'bg-emerald-600' },
          { k: 'propertyfinder', label: 'PF Residential', color: 'bg-orange-500' },
          { k: 'propertyfinder_commercial', label: 'PF Commercial', color: 'bg-indigo-600' },
        ].map(s => {
          const active = sourceFilter === s.k;
          return (
            <button key={s.k || 'all'}
              onClick={() => { setSourceFilter(s.k); setPage(1); }}
              data-testid={`listings-source-${s.k || 'all'}`}
              className={`px-3 py-1 rounded-full text-xs transition border flex-shrink-0 whitespace-nowrap ${
                active ? `${s.color} text-white border-transparent shadow-sm`
                       : 'bg-white/85 hover:bg-white border-white/70 text-slate-700'
              }`}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Community chip filter */}
      {communityList.length > 0 && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
            Community
            {selectedCommunities.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-[#679286] text-white text-[9px]">
                {selectedCommunities.length} selected
              </span>
            )}
          </div>
          <div className="flex md:flex-wrap gap-1.5 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1 md:overflow-visible md:mx-0 md:px-0">
            {(showAllComms ? communityList : communityList.slice(0, 20)).map(c => {
              const active = selectedCommunities.includes(c.community);
              return (
                <button
                  key={c.community}
                  onClick={() => toggleCommunity(c.community)}
                  data-testid={`community-chip-${c.community.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs border transition flex-shrink-0 whitespace-nowrap ${
                    active
                      ? 'bg-[#679286] text-white border-[#679286] shadow-sm'
                      : 'bg-white/85 hover:bg-white border-white/70 text-slate-700'
                  }`}
                >
                  {c.community}
                  <span className={`text-[9px] ${active ? 'opacity-80' : 'text-slate-500'}`}>
                    {c.count.toLocaleString()}
                  </span>
                </button>
              );
            })}
            {communityList.length > 20 && (
              <button
                onClick={() => setShowAllComms(!showAllComms)}
                data-testid="community-toggle"
                className="text-xs text-[#679286] underline hover:no-underline px-2 flex-shrink-0"
              >
                {showAllComms ? '← Show fewer' : `Show all ${communityList.length} →`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Secondary filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500">
          <Filter size={11}/> More filters
          {activeFilterCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-[#679286] text-white text-[9px]">
              {activeFilterCount}
            </span>
          )}
        </div>
        <select value={propertyType} onChange={(e) => { setPropertyType(e.target.value); setPage(1); }}
          data-testid="listings-type"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
          <option value="">All types</option>
          {(segment === 'commercial' ? COMMERCIAL_TYPES : PROP_TYPES).map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={bedrooms} onChange={(e) => { setBedrooms(e.target.value); setPage(1); }}
          data-testid="listings-beds"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
          <option value="">Any beds</option>
          <option value="0">Studio</option>
          <option value="1">1 BR</option>
          <option value="2">2 BR</option>
          <option value="3">3 BR</option>
          <option value="4">4 BR</option>
          <option value="5">5+ BR</option>
        </select>
        <select value={completion} onChange={(e) => { setCompletion(e.target.value); setPage(1); }}
          data-testid="listings-completion"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
          <option value="">Any status</option>
          <option value="ready">Ready</option>
          <option value="off_plan">Off-plan</option>
        </select>
        <div className="inline-flex items-center gap-1 bg-white/85 border border-white/70 rounded-full pl-3 pr-1 py-1">
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">AED</span>
          <input type="number" placeholder="Min" value={minPrice}
            onChange={(e) => { setMinPrice(e.target.value); setPage(1); }}
            data-testid="listings-min-price"
            className="w-20 text-xs bg-transparent outline-none px-1"/>
          <span className="text-slate-400">–</span>
          <input type="number" placeholder="Max" value={maxPrice}
            onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }}
            data-testid="listings-max-price"
            className="w-20 text-xs bg-transparent outline-none px-1"/>
        </div>
        <div className="inline-flex items-center gap-1 bg-white/85 border border-white/70 rounded-full pl-3 pr-1 py-1">
          <span className="text-[10px] text-slate-500 uppercase tracking-widest">sqft</span>
          <input type="number" placeholder="Min" value={minSqft}
            onChange={(e) => { setMinSqft(e.target.value); setPage(1); }}
            data-testid="listings-min-sqft"
            className="w-16 text-xs bg-transparent outline-none px-1"/>
          <span className="text-slate-400">–</span>
          <input type="number" placeholder="Max" value={maxSqft}
            onChange={(e) => { setMaxSqft(e.target.value); setPage(1); }}
            data-testid="listings-max-sqft"
            className="w-16 text-xs bg-transparent outline-none px-1"/>
        </div>
        <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
          data-testid="listings-sort"
          className="px-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs">
          {SORTS.map(s => <option key={s.k} value={s.k}>{s.label}</option>)}
        </select>
        <div className="relative">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title/building…"
            data-testid="listings-search"
            className="pl-8 pr-3 py-1.5 rounded-full bg-white/85 border border-white/70 text-xs w-48 outline-none focus:ring-2 focus:ring-[#679286]/30"/>
        </div>
        {activeFilterCount > 0 && (
          <button onClick={clearAllFilters}
            data-testid="listings-clear"
            className="text-[10px] uppercase tracking-widest text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <X size={10}/> Clear all
          </button>
        )}
        {busy && <Loader2 size={14} className="animate-spin text-slate-400 ml-auto"/>}
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400" data-testid="listings-empty">
          {items.length === 0 ? (
            <>
              <Home size={28} className="mx-auto mb-2 text-slate-300"/>
              No live listings yet in this workspace.{isLive ? ' Click “Sync now” to pull from Bayut.' : ''}
            </>
          ) : `No listings match "${q}".`}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map(l => <ListingCard key={l.id} l={l}/>)}
        </motion.div>
      )}

      {/* Pagination */}
      {total > 24 && (
        <div className="flex items-center justify-between mt-5">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
            data-testid="listings-prev"
            className="btn-ghost text-xs disabled:opacity-40">← Prev</button>
          <div className="text-xs text-slate-500">Page {page} of {totalPages} · {total.toLocaleString()} listings</div>
          <button onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}
            data-testid="listings-next"
            className="btn-ghost text-xs disabled:opacity-40">Next →</button>
        </div>
      )}
    </div>
  );
}
